import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    useLocation,
    useMatch,
    useParams,
    useSearchParams,
} from 'react-router-dom';

import { useAuth } from 'hooks/useAuth';
import { useMemberGroupData } from 'hooks/useMemberGroupData';
import { useNavigateWithParams } from 'hooks/useNavigateWithParams';
import { useResponsiveScreenSizes } from 'hooks/useResponsiveScreenSizes';

import { sortModules } from 'queries/groups';

import {
    formatDateTime,
    getDayCountBetweenDates,
    getRelationshipToToday,
    toDate,
} from 'libs/utils';

import { Tab, TabControls } from 'components/ui/TabControls';
import { MarkdownWrapper } from 'components/ui/MarkdownWrapper';
import { Tag } from 'components/ui/Tag';
import { ActionCard } from 'components/ui/ActionCard';
import { Button } from 'components/ui/Button';

import { colors, Fonts, spacing, transparencies } from 'styles/theme';
import {
    Card,
    ContentScroller,
    HorizontalFlexWrapper,
    Bumper,
} from 'styles/shared';
import { HeaderWrapper } from 'styles/sessionDetails';

interface SessionDetailsProps {
    isPursuit?: boolean;
}

export const SessionDetails = ({ isPursuit = false }: SessionDetailsProps) => {
    const { userProfile, userId } = useAuth();
    const { closings, group, scheduledSessions, sessionDetails } =
        useMemberGroupData();
    const { isMobileScreenSize } = useResponsiveScreenSizes();
    const { sessionNumber } = useParams();
    const { pathname } = useLocation();
    const navigateWithParams = useNavigateWithParams();

    const [, setSearchParams] = useSearchParams();
    const [initialPath, setInitialPath] = useState<string>('');

    const isScheduledSession = sessionNumber == null;

    const baseRoute = isScheduledSession
        ? `/${isPursuit ? 'pursuit' : 'member'}/scheduled-session`
        : `/${isPursuit ? 'pursuit' : 'member'}/sessions/${sessionNumber}`;

    const baseWithNoTabRoute = useMatch(`${baseRoute}`);
    const descriptionRoute = useMatch(`${baseRoute}/description/*`);
    const reviewRoute = useMatch(`${baseRoute}/review/*`);
    const applyRoute = useMatch(`${baseRoute}/apply/*`);
    const reflectRoute = useMatch(`${baseRoute}/reflect/*`);

    const onGoToSessionList = useCallback(() => {
        navigateWithParams(`/${isPursuit ? 'pursuit' : 'member'}/sessions`);
    }, [isPursuit, navigateWithParams]);

    const sessionDate = useMemo(
        () =>
            isScheduledSession
                ? group?.next_session_date ?? ''
                : scheduledSessions?.[parseInt(sessionNumber, 10) - 1]?.start ??
                  null,
        [
            group?.next_session_date,
            isScheduledSession,
            scheduledSessions,
            sessionNumber,
        ]
    );

    const sessionDetailsDatum = useMemo(
        () =>
            sessionDetails[
                (isScheduledSession
                    ? group?.session_counter ?? 1
                    : parseInt(sessionNumber, 10)) - 1
            ] ?? null,
        [
            group?.session_counter,
            isScheduledSession,
            sessionDetails,
            sessionNumber,
        ]
    );

    const sortedModules = useMemo(
        () =>
            sortModules(
                sessionDetailsDatum?.session_details_modules ?? [],
                sessionDetailsDatum?.modules ?? []
            ),
        [
            sessionDetailsDatum?.modules,
            sessionDetailsDatum?.session_details_modules,
        ]
    );

    const sessionContent = useMemo(
        () => ({
            description:
                (sessionDetailsDatum?.description?.length ?? 0) > 0
                    ? [sessionDetailsDatum?.description ?? '']
                    : sortedModules?.map(
                          (module) => module?.description ?? ''
                      ) ?? [],
            review:
                sortedModules
                    ?.filter((module) => module?.review_content != null)
                    .map((module) => module?.review_content ?? '') ?? [],
            apply:
                sortedModules
                    ?.filter((module) => module?.apply_content != null)
                    .map((module) => module?.apply_content ?? '') ?? [],
            reflect:
                sortedModules
                    ?.filter((module) => module?.reflect_content != null)
                    .map((module) => module?.reflect_content ?? '') ?? [],
        }),
        [sessionDetailsDatum?.description, sortedModules]
    );

    const sessionDetailsTabs = useMemo(() => {
        const tabsToReturn: Array<Tab> = [];
        if (sessionDetails.length === 0) return tabsToReturn;

        if (sessionContent.description.length > 0)
            tabsToReturn.push({
                text: 'Description',
                isSelected: descriptionRoute != null,
                onClick: () => navigateWithParams(`${baseRoute}/description`),
            });
        if (sessionContent.review.length > 0)
            tabsToReturn.push({
                text: 'Review',
                isSelected: reviewRoute != null,
                onClick: () => navigateWithParams(`${baseRoute}/review`),
            });
        if (sessionContent.apply.length > 0)
            tabsToReturn.push({
                text: 'Apply',
                isSelected: applyRoute != null,
                onClick: () => navigateWithParams(`${baseRoute}/apply`),
            });
        if (sessionContent.reflect.length > 0)
            tabsToReturn.push({
                text: 'Reflect',
                isSelected: reflectRoute != null,
                onClick: () => navigateWithParams(`${baseRoute}/reflect`),
            });

        return tabsToReturn;
    }, [
        sessionDetails.length,
        sessionContent,
        descriptionRoute,
        reviewRoute,
        applyRoute,
        reflectRoute,
        navigateWithParams,
        baseRoute,
    ]);

    useEffect(() => {
        setInitialPath(pathname);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (sessionDetailsTabs.length === 0 && baseWithNoTabRoute == null) {
            navigateWithParams(baseRoute);
        } else if (
            sessionDetailsTabs.length > 0 &&
            baseWithNoTabRoute != null
        ) {
            navigateWithParams(initialPath);
        }
    }, [
        baseRoute,
        baseWithNoTabRoute,
        initialPath,
        navigateWithParams,
        sessionDetailsTabs.length,
    ]);

    const sessionContentMarkdownStrings: Array<string> = useMemo(() => {
        if (descriptionRoute != null) return sessionContent.description;
        if (reviewRoute != null) return sessionContent.review;
        if (applyRoute != null) return sessionContent.apply;
        if (reflectRoute != null) return sessionContent.reflect;
        return [];
    }, [
        descriptionRoute,
        reviewRoute,
        applyRoute,
        reflectRoute,
        sessionContent,
    ]);

    const daysUntilSessionDate = useMemo(() => {
        const today = new Date();
        const nextSessionDate = toDate(group?.next_session_date) ?? today;
        return getDayCountBetweenDates(today, nextSessionDate);
    }, [group?.next_session_date]);

    const actionCardNode = useMemo(() => {
        const parsedSessionDate = toDate(sessionDate);
        if (
            parsedSessionDate == null ||
            getRelationshipToToday(parsedSessionDate) === 'upcoming'
        )
            return null;

        const completedClosing = closings.find(
            (closing) =>
                closing.created_by === userId &&
                closing.session_date === sessionDate
        );
        const exercisesFrameworks =
            sortedModules?.flatMap((module) =>
                module?.exercises_frameworks.filter(
                    (e) => e.type === 'Exercise'
                )
            ) ?? [];

        if (completedClosing != null && exercisesFrameworks.length === 0)
            return null;

        const nextDateParsed = toDate(group?.next_session_date);

        const actionItems =
            completedClosing != null ||
            sessionDate == null ||
            (sessionDate !== group?.previous_session_date &&
                sessionDate !== group?.next_session_date) ||
            (sessionDate === group?.previous_session_date &&
                nextDateParsed &&
                getRelationshipToToday(nextDateParsed) !== 'upcoming')
                ? []
                : [
                      {
                          text: 'Session Closing',
                          onClick: () =>
                              setSearchParams({
                                  modalContent: 'closing',
                                  sessionDate: sessionDate,
                              }),
                      },
                  ];

        exercisesFrameworks.forEach((exerciseFramework) => {
            switch (exerciseFramework?.link) {
                case 'The Grand Retro':
                    actionItems.push({
                        text: 'Take Grand Retro',
                        onClick: () =>
                            navigateWithParams(
                                `/${
                                    isPursuit ? 'pursuit' : 'member'
                                }/insights/grand-retro`
                            ),
                    });
                    break;
                case 'Feedback Tool':
                    actionItems.push({
                        text: 'Feedback Tool',
                        onClick: () =>
                            setSearchParams({
                                modalContent: 'feedback',
                            }),
                    });
                    break;
                default:
                    return;
            }
        });

        return actionItems.length === 0 ? null : (
            <ActionCard actionItems={actionItems} />
        );
    }, [
        closings,
        group?.next_session_date,
        group?.previous_session_date,
        isPursuit,
        navigateWithParams,
        sessionDate,
        setSearchParams,
        sortedModules,
        userId,
    ]);

    const parsedDisplayDate =
        isScheduledSession && group?.next_session_date != null
            ? toDate(group.next_session_date)
            : toDate(
                  scheduledSessions?.[parseInt(sessionNumber ?? '', 10) - 1]
                      ?.start ?? null
              );

    return (
        <>
            <ContentScroller>
                {isScheduledSession ? (
                    <HeaderWrapper>
                        <Fonts.Heading5>{`Hello, ${
                            userProfile?.full_name?.split(' ')[0] ??
                            'Grand Explorer'
                        }`}</Fonts.Heading5>
                        <Bumper $minHeight={spacing.xxxSmall} />
                        {userProfile?.imagined_future ? (
                            <Fonts.MediumQuote>
                                {`When you joined The Grand, you shared that 6 months from now you hoped you would be... “${userProfile.imagined_future}”`}
                            </Fonts.MediumQuote>
                        ) : (
                            <>
                                <Fonts.MediumQuote>{`“Imagination is the beginning of creation...”`}</Fonts.MediumQuote>
                                <Bumper $minHeight={spacing.xxSmall} />
                                <Fonts.Medium>{`– George Bernard Shaw`}</Fonts.Medium>
                            </>
                        )}
                        <Bumper />
                    </HeaderWrapper>
                ) : (
                    <>
                        <Bumper
                            $minHeight={spacing.xxxLarge}
                            $shouldHideOnMobile
                        />
                        <Bumper
                            $minHeight={spacing.xxxLarge}
                            $shouldHideOnMobile
                        />
                        <Bumper
                            $minHeight={spacing.xxSmall}
                            $shouldHideOnDesktop
                        />
                    </>
                )}

                <Card
                    $backgroundColor={
                        isPursuit ? transparencies.white95 : colors.white
                    }
                >
                    {!isScheduledSession && (
                        <>
                            <HorizontalFlexWrapper>
                                <Button
                                    onClick={onGoToSessionList}
                                    isBackButton
                                />
                                <Bumper $minWidth={spacing.xSmall} />
                                <Fonts.Medium
                                    $color={colors.grandGreen}
                                >{`All sessions`}</Fonts.Medium>
                            </HorizontalFlexWrapper>
                            <Bumper />
                        </>
                    )}

                    {actionCardNode && isMobileScreenSize && (
                        <>
                            {actionCardNode}
                            <Bumper />
                        </>
                    )}

                    <Fonts.XLarge $color={colors.mountainAsh}>
                        {`Session ${
                            isScheduledSession
                                ? group?.session_counter
                                : sessionNumber
                        }`}
                    </Fonts.XLarge>
                    <Bumper $minHeight={spacing.xxxSmall} />

                    <HorizontalFlexWrapper>
                        <Fonts.Medium $color={colors.grandGreen}>
                            {parsedDisplayDate
                                ? formatDateTime(parsedDisplayDate)
                                : 'TBD'}
                        </Fonts.Medium>
                        {daysUntilSessionDate >= 0 && (
                            <Tag
                                text={
                                    daysUntilSessionDate === 0
                                        ? 'Today'
                                        : `In ${daysUntilSessionDate} days`
                                }
                            />
                        )}
                    </HorizontalFlexWrapper>

                    {sessionDetailsDatum?.title && (
                        <>
                            <Bumper $minHeight={spacing.medium} />
                            <Fonts.Heading5>
                                {sessionDetailsDatum.title}
                            </Fonts.Heading5>
                        </>
                    )}

                    {sessionDetailsTabs.length > 0 && (
                        <>
                            <Bumper />
                            <TabControls tabs={sessionDetailsTabs} />
                            <Bumper $minHeight={spacing.xLarge} />
                            {sessionContentMarkdownStrings.map(
                                (markdownString, index) => (
                                    <MarkdownWrapper
                                        key={index}
                                        markdownString={markdownString}
                                        $shouldReduceTopMargin={
                                            descriptionRoute != null &&
                                            index > 0
                                        }
                                    />
                                )
                            )}
                        </>
                    )}
                </Card>
                <Bumper $minHeight={spacing.xxxLarge} />
            </ContentScroller>

            {actionCardNode && !isMobileScreenSize && (
                <>
                    <Bumper $minWidth={spacing.small} />
                    <div>
                        <Bumper $minHeight={spacing.xxxLarge} />
                        <Bumper $minHeight={spacing.xxxLarge} />
                        {actionCardNode}
                    </div>
                </>
            )}
        </>
    );
};
